/**
 * Thanksgiving emotes — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingEmotesPack: ToilePack = {
    slug: 'thanksgiving-emotes',
    category: 'season',
    name: 'Thanksgiving emotes',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'full', label: 'Full belly', subject: 'A round face with closed content eyes and puffed cheeks holding its belly, built to read tiny, isolated'},
        {key: 'sideeye', label: 'Turkey side-eye', subject: 'A round turkey face with narrowed eyes glancing sideways, built to read tiny, isolated'},
        {key: 'pielove', label: 'Pie love', subject: 'A round face with heart eyes beside a slice of pie, built to read tiny, isolated'},
    ],
    styles: [
        {key: 'harvest', label: 'Harvest flat', style: 'flat harvest illustration, simplified shapes, no outlines, matte colour', palette: 'Mustard, rust, pumpkin orange, olive and cream', framing: 'Centered on a solid cream background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage americana', style: 'vintage americana illustration, halftone grain, slight misregistration, warm', palette: 'Faded rust, brown, mustard and cream', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolour illustration, loose edges, paper texture showing', palette: 'Warm autumn washes of orange, ochre and brown', framing: 'Centered with white paper around it'},
    ],
    directions: ['a sleepy after-dinner face', 'as a wink instead', 'in a single bold outline'],
    directionHint: 'try: a sleepy after-dinner face',
    seo: {
        title: 'Thanksgiving Emotes: Full Belly, Turkey & Pie Love',
        metaDescription: 'Nine Thanksgiving emotes — full-belly face, turkey side-eye, pie love — built to read at 28 pixels, in harvest flat, vintage, and watercolor styles.',
        h1: 'Thanksgiving twitch emotes: full-belly face, turkey side-eye, pie love',
        tagline: 'Nine emotes, three moods, three treatments each.',
        intro: 'Nine chat emotes for Thanksgiving: a content full-belly face holding its stomach, a turkey with a narrowed side-eye glance, and a face with heart eyes beside a slice of pie, each in a harvest flat, a vintage americana, and a watercolor treatment. Built to survive shrinking down to actual emote size in chat.',
        useCases: [
            "a Twitch channel's Thanksgiving emote set",
            "a Discord server's seasonal emoji pack",
            'a sticker reaction for chat and DMs',
            'a small badge on a stream overlay',
        ],
        faq: [
            {q: 'What size do the emotes work at?', a: 'Each design is built and checked to stay readable down to the roughly 28-pixel size Twitch and Discord render emotes at.'},
            {q: 'How many emotes do I get?', a: 'Nine files: three expressions — full belly, turkey side-eye, pie love — each in three visual styles.'},
            {q: 'Can I use these on Twitch and Discord both?', a: 'Yes — the files are plain transparent-background images you upload wherever the platform accepts custom emotes or emoji.'},
            {q: 'Is there text or a watermark on the emotes?', a: 'No — every emote is the face alone with a clean background, ready to upload as-is.'},
        ],
    },
}
